define('viewModels/privileged/qm/ConversationDetailsView',[
    'lib/knockout',
    'lib/lodash4',
    'lib/bluebird',
    'lib/clipboard.min',
    'moment',
    'services/authService',
    'services/callService',
    'services/loggingService',
    'services/userService',
    'services/externalContactService',
    'services/featureToggleService',
    'services/qm/qualityAuth',
    'services/qualityManagementService',
    'services/conversationsProtectionService',
    'services/composer/evaluationFormService',
    'services/queueAdmin/wrapupCodeService',
    'services/toastService',
    'services/navHelper',
    'viewModels/ViewModel',
    'viewModels/privileged/qm/policies/editors/DateEditorView',
    'viewModels/privileged/qm/util/FileStateEnum',
    'util/dateTimeConstant',
    'util/errorCallbackFactory',
    'services/qm/qualityBroadcast',
    'viewModels/privileged/qm/util/BroadCastEnum',
    //no references required
    'extensions/i18n/knockout.i18n'
], function (ko,
             _,
             Promise,
             Clipboard,
             moment,
             authService,
             callService,
             LOG,
             userService,
             externalContactService,
             featureToggleService,
             qualityAuth,
             qualityManagementService,
             conversationsProtectionService,
             evaluationFormService,
             wrapUpCodeService,
             toasts,
             navHelper,
             ViewModel,
             DateEditorView,
             FileStateEnum,
             dateTimeConstant,
             errorCallbackFactory,
             qualityBroadcast,
             BroadCastEnum) {
    'use strict';

    var checkImages = function(avatarImages){
        // if any image size has a "no_img" value then return false so the default image will show
        // the reason for returning false and not one with an image if it exists to to keep images consistent
        // in the template we care about the x96 size, for brevity sake
        return !_.includes(avatarImages(), "no_img");
    };

    function participantHasCalls(participant) {
        return participant.calls && participant.calls.length > 0;
    }

    function participantHasConnectedCalls(participant) {
        return participantHasCalls(participant) && _.some(participant.calls, {'state': 'connected'});
    }

    //Gets initialized in constructor of ConversationDetailsView
    var directionStrings = {};

    var getLocalizedDirection = function (dir) {
            if (_.isEmpty(dir)) {
                return dir;
            }

            dir = dir.toLowerCase();

            if (dir.indexOf('inbound') > -1 && dir.indexOf('outbound') > -1) {
                return directionStrings.BOTH;
            } else if (dir === "inbound") {
                return directionStrings.INBOUND;
            } else if (dir === "outbound") {
                return directionStrings.OUTBOUND;
            }
        };

    var getWrapupName = function (wrapup) {
        var wrapupName = wrapup.name;
        if (!wrapupName && wrapup.code && _.startsWith(wrapup.code, "ININ-")) {
            wrapupName = wrapup.code;
        }
        return wrapupName;
    };

    var MESSAGE_SUBTYPE_SMS = "sms";

    var ParticipantView = ViewModel.extend(function (conversationId, participant, analyticsParticipantV6,
        isCurrentUserOnCall, hasMonitorCallPermission, options) {
        var self = this;
        var endTime = ko.observable(participant.endTime);
        var monitoredParticipantId = ko.observable(participant.monitoredParticipantId);
        var hasCalls = ko.observable(participantHasCalls(participant));
        var hasConnectedCalls = ko.observable(participantHasConnectedCalls(participant));
        self.conversationId = conversationId;
        self.requestInProgress = ko.observable(false);
        self.name = participant.name;
        self.dnis = ko.i18n.formatPhoneAddress(participant.dnis);
        self.ani = ko.i18n.formatPhoneAddress(participant.ani);

        var conversationParticipant = ko.observable(participant);
        var analyticsParticipant = ko.observable(analyticsParticipantV6);
        var wrapUpCodeNamesById = {};

        self.address = ko.i18n.formatPhoneAddress(participant.address);
        self.user = ko.observable();
        self.userImages = ko.observable();
        self.participantId = participant.id;
        self.deleteDate = ko.observable();
        self.archiveDate = ko.observable();
        self.userId = participant.userId;
        self.disconnectType = ko.observable();
        self.externalContact = ko.observable();
        self.externalContactId = participant.externalContactId;
        self.id = participant.id;

        var direction;
        var disconnectType;

        if (!_.isEmpty(participant.calls) && !_.isUndefined(participant.calls[0].direction)) {
            direction = participant.calls[0].direction;
            disconnectType = participant.calls[0].disconnectType;
        } else if (!_.isEmpty(participant.chats)) {
            direction = participant.chats[0].direction;
            disconnectType = participant.chats[0].disconnectType;
        } else if (!_.isEmpty(participant.emails) && !_.isUndefined(participant.emails[0].direction)) {
            direction = participant.emails[0].direction;
            var lastEmail = participant.emails[participant.emails.length - 1];
            disconnectType = lastEmail.disconnectType;
        } else if (!_.isEmpty(participant.messages)) {
            var lastMessage = participant.messages[participant.messages.length - 1];
            disconnectType = lastMessage.disconnectType;
        }

        self.direction = getLocalizedDirection(direction);

        if (disconnectType) {
            var disconnectTypeKey;
            var PREFIX = "conversationTimeline.tooltip.disconnectType.";
            disconnectType = disconnectType.toLowerCase();

            if (disconnectType.indexOf('transfer') !== -1) {
                disconnectTypeKey = PREFIX + 'transfer';
            } else if (ko.i18n.keyExists(PREFIX + disconnectType)) {
                disconnectTypeKey = PREFIX + disconnectType;
            } else {
                disconnectTypeKey = PREFIX + 'other';
            }

            disconnectType = ko.i18n.renderString(disconnectTypeKey);
            self.disconnectType(disconnectType);
        }

        if (options.expandUser && participant.userId) {
            userService.getUser(participant.userId)
            .then(function (user) {
                self.user(user);
                if (user.avatarImages && user.avatarImages() && checkImages(user.avatarImages)) {
                    self.userImages(user.avatarImages());
                    var imagesMap = {};
                    imagesMap[participant.userId] = user.avatarImages();
                    qualityBroadcast.publish(conversationId, BroadCastEnum.USER_IMAGES_AVAILABLE, imagesMap);
                }
            });
        }

        if (participant.externalContactId) {
            if (authService.hasAuthorization('perm:externalContacts:contact:view')) {
                externalContactService.getCachedExternalContact(participant.externalContactId)
                .then(function(externalContact) {
                    self.externalContact(externalContact);
                });
            }
        }

        // check to see if there is an active user call that is monitoring this participant
        var monitoredCall = self.computed(function () {
            return _.find(callService.activeUserCalls(), function (call) {
                return self.conversationId === call.id &&
                    call.isMonitor() &&
                    call.participantForCurrentUser().monitoredParticipantId() === participant.id;
            });
        });

        self.updateParticipant = function (updatedParticipant, updatedAnalyticsParticipant) {
            if (participant.id === updatedParticipant.id) {
                conversationParticipant(updatedParticipant);
                endTime(updatedParticipant.endTime);
                monitoredParticipantId(updatedParticipant.monitoredParticipantId);
                hasCalls(participantHasCalls(updatedParticipant));
                hasConnectedCalls(participantHasConnectedCalls(updatedParticipant));
            } else {
                LOG.error("The updatedParticipant id does not match the current participant id.", {
                    updatedParticipantId: updatedParticipant.id,
                    currentParticipantId: participant.id
                });
            }

            if (updatedAnalyticsParticipant && (participant.id === updatedAnalyticsParticipant.id)) {
                analyticsParticipant(analyticsParticipant);
            }
        };

        self.canMonitor = self.computed(function () {
            return !isCurrentUserOnCall() && ((!endTime() && hasCalls()) || hasConnectedCalls());
        });

        self.isPermittedToMonitor = self.computed(function () {
            return hasMonitorCallPermission();
        });

        self.isMonitored = self.computed(function () {
            return !!monitoredCall();
        });

        self.isMonitor = self.computed(function () {
            return !!monitoredParticipantId();
        });

        self.isActive = self.computed(function () {
            return !endTime();
        });

        self.isInternal = function () {
            if (participant.purpose) {
                return (participant.purpose.toLowerCase() === 'user' || participant.purpose.toLowerCase() === 'agent') &&
                    !self.isMachine();
            } else if (participant.participantType &&
                        participant.participantType.toLowerCase() === "internal") {
                // workaround for https://inindca.atlassian.net/browse/AR-2731
                return !!participant.userId;
            }
            return false;
        };

        self.isExternal = function () {
            if (participant.purpose) {
                return (participant.purpose.toLowerCase() === 'external' || participant.purpose.toLowerCase() === 'customer') &&
                    !self.isMachine();
            } else if (participant.participantType &&
                        participant.participantType.toLowerCase() === "external") {
                // workaround for https://inindca.atlassian.net/browse/AR-2731
                return true;
            }
            return false;
        };

        self.isMachine = function () {
            if (participant.purpose) {
                var participantPurpose = participant.purpose.toLowerCase();
                return participantPurpose === 'acd' ||
                       participantPurpose === 'ivr' ||
                       participantPurpose === 'dialer.system' ||
                       participantPurpose === 'voicemail' ||
                       participantPurpose === 'fax';
            }
            return false;
        };

        self.hasParticipantName = function() {
            return participant.name && participant.name.trim().length > 0;
        };

        self.hasWrapUpCode = function() {
            return !!participant.wrapup;
        };

        self.hasName = ko.observable(true);

        self.wrapUpCodeName = (function () {
            var name;
            if (self.hasWrapUpCode()) {
                name = getWrapupName(participant.wrapup);
                if (!name) {
                    self.hasName(false);
                }
            }
            return name;
        }());

        self.hasWrapUpNotes = function() {
            return participant.wrapup && participant.wrapup.notes;
        };

        self.wrapUpNotes = function() {
            if(self.hasWrapUpNotes()) {
                return participant.wrapup.notes;
            } else {
                return '';
            }
        };

        self.awaitingWrapup = function() {
            return !self.hasWrapUpCode() && participant.wrapupRequired;
        };

        self.monitor = function () {
            self.requestInProgress(true);
            return callService.monitorCall(self.conversationId, participant.id, true)
                        .catch(errorCallbackFactory.createCallback({ namespace: "conversationDetails.monitorRequest"}))
                        .finally(function () {
                            self.requestInProgress(false);
                        });
        };

        self.stopMonitor = function () {
            if (monitoredCall()) {
                self.requestInProgress(true);
                return callService.endCall(monitoredCall().id, monitoredCall().participantForCurrentUser().id)
                    .catch(errorCallbackFactory.createCallback({ namespace: "conversationDetails.stopMonitorRequest"}))
                    .finally(function () {
                        self.requestInProgress(false);
                    });
            }
        };

        self.nameDisplayString = self.computed(function () {
            var calculatedDisplayString = "";
            if (self.isExternal()) {
                if (self.externalContact()) {
                    calculatedDisplayString = ko.i18n.renderString({
                        'name': 'conversationDetails.externalContact.nameFormat',
                        'data': {
                            'firstName': self.externalContact().firstName,
                            'lastName': self.externalContact().lastName
                        }
                    });
                }
            }
            return (calculatedDisplayString || self.name);
        });

        self.getMessageType = self.computed(function () {
            return participant.messages && participant.messages.length && participant.messages[0].type;
        });

        // Set this when there is a user for the participant and the participant name is not the user name.
        self.userDisplayString = self.computed(function () {
            if (self.user() && self.user().name) {
                if (self.nameDisplayString() !== self.user().name()) {
                    return self.user().name();
                }
            }
        });

        var formatAddress = function (rawValue) {
            if (rawValue.indexOf("sip:") === 0) {
                return ko.i18n.renderString('conversationDetails.participantAddress.sipDevice');
            } else {
                return ko.i18n.formatPhoneAddress(rawValue);
            }
        };

        var analyticsAddressDisplayString = self.computed(function () {
            var calculatedDisplayString = null;
            if (analyticsParticipant()) {
                var lastTelephonySession = _.findLast(analyticsParticipant().sessions, function(session) {
                    return (session.dnis && session.ani && session.direction);
                });
                if (lastTelephonySession) {
                    var address = null;
                    var lastDirection = lastTelephonySession.direction.toLowerCase();
                    if (self.isInternal()) {
                        if (lastDirection.toLowerCase() === "inbound") {
                            address = lastTelephonySession.dnis;
                        } else {
                            address = lastTelephonySession.ani;
                        }
                    } else if (self.isExternal()) {
                        if (lastDirection.toLowerCase() === "inbound") {
                            address = lastTelephonySession.ani;
                        } else {
                            address = lastTelephonySession.dnis;
                        }
                    }

                    if (address) {
                        calculatedDisplayString = formatAddress(address);
                    }
                }
            }
            return calculatedDisplayString;
        });

        self.addressDisplayString = self.computed(function () {
            // Try to calculate the display from the direct analytics response.
            var calculatedDisplayString = analyticsAddressDisplayString();

            // Fallback to finding the display from the merged conversation endpoint response.
            if (!calculatedDisplayString) {
                calculatedDisplayString = self.address;
                if (participant.messages && participant.messages.length > 0) {
                    if (self.getMessageType() === MESSAGE_SUBTYPE_SMS) {
                        var smsMessageWithAddress = _.find(participant.messages, 'fromAddress');
                        if (smsMessageWithAddress) {
                            var fromAddress = smsMessageWithAddress.fromAddress.addressNormalized;
                            calculatedDisplayString = ko.i18n.formatPhoneAddress(fromAddress);
                        }
                    } else {
                        calculatedDisplayString = "";
                        var messageWithAddress = _.find(participant.messages, 'fromAddress');
                        if (messageWithAddress && messageWithAddress.fromAddress.addressDisplayable) {
                            calculatedDisplayString = messageWithAddress.fromAddress.addressDisplayable;
                        }
                    }
                } else if (participant.calls && participant.calls.length > 0) {
                   if (participant.address && (participant.address.indexOf("sip:") === 0)) {
                        calculatedDisplayString = ko.i18n.renderString('conversationDetails.participantAddress.sipDevice');
                    } else {
                        if (self.isInternal()) {
                            calculatedDisplayString = (direction.toLowerCase() === "inbound") ? self.dnis : self.ani;
                        } else if (self.isExternal()) {
                            calculatedDisplayString = (direction.toLowerCase() === "inbound") ? self.ani : self.dnis;
                        }
                    }
                } else if (participant.chats && participant.chats.length > 0) {
                    // Show nothing instead of the room jid that's in the address
                    calculatedDisplayString = "";
                }
            }

            return calculatedDisplayString;
        });

        self.extraAddressDisplayInformation = self.computed(function () {
            if (participant.calls &&
                participant.calls.length > 0 &&
                participant.address &&
                (participant.address.indexOf("sip:") === 0)) {

                return self.address;
            }
            return "";
        });

        self.displayString = self.computed(function () {
            return self.nameDisplayString() || self.addressDisplayString();
        });

        self.isAddressCopyable = self.computed(function () {
            return Clipboard.isSupported() && self.addressDisplayString() && !self.extraAddressDisplayInformation();
        });

        self.pastWrapUpCodesView = {
            popoverTitle: self.computed(function () {
                return ko.i18n.renderString({
                    'name': 'conversationDetails.pastWrapUps.title',
                    'data': {
                        'participantName': self.userDisplayString() || self.name
                    }
                });
            }),
            pastWrapUpCodes: self.computed(function () {
                var pastWrapUpCodes = [];
                if (analyticsParticipantV6 && analyticsParticipantV6.sessions) {
                    _.each(analyticsParticipantV6.sessions, function (session){
                        if (session && session.segments && session.mediaType !== "callback") {
                            _.each(session.segments, function (segment){
                                if (segment.segmentType === "wrapup" && segment.wrapUpCode) {
                                    if (!wrapUpCodeNamesById[segment.wrapUpCode]) {
                                        wrapUpCodeNamesById[segment.wrapUpCode] = ko.observable();
                                        wrapUpCodeService.getWrapupCodeById(segment.wrapUpCode).then(function(wrapUpFetchResult){
                                            wrapUpCodeNamesById[segment.wrapUpCode](getWrapupName(wrapUpFetchResult));
                                        }).catch(function (err) {
                                            LOG.error("Error fetching wrap up code", err);
                                            if (_.startsWith(segment.wrapUpCode, "ININ-")) {
                                                wrapUpCodeNamesById[segment.wrapUpCode](getWrapupName({
                                                    'code': segment.wrapUpCode
                                                }));
                                            }
                                        });
                                    }

                                    var wrapUpNote = ko.observable(segment.wrapUpNote);
                                    pastWrapUpCodes.push({
                                        wrapUpCodeName: wrapUpCodeNamesById[segment.wrapUpCode],
                                        wrapUpCodeId: segment.wrapUpCode,
                                        wrapUpNote: wrapUpNote,
                                        hasWrapUpCodeName: ko.computed(function () {
                                            return !!wrapUpCodeNamesById[segment.wrapUpCode]();
                                        }),
                                        hasWrapUpNotes: ko.computed(function () {
                                            return !!wrapUpNote();
                                        })
                                    });
                                }
                            });
                        }
                    });
                }
                return pastWrapUpCodes;
            })
        };

        self.hasAdditionalWrapUpCodes = ko.computed(function () {
            return self.pastWrapUpCodesView.pastWrapUpCodes() && self.pastWrapUpCodesView.pastWrapUpCodes().length > 1;
        });
    });

    var ConversationDetailsView = ViewModel.extend(function (conversationObservable, conversationDetailsV6, options) {
        var self = this;
        options = _.assign({ expandUser: true }, options);
        directionStrings = {
            INBOUND: ko.i18n.renderString('interactionsList.direction.inbound'),
            OUTBOUND: ko.i18n.renderString('interactionsList.direction.outbound'),
            BOTH: ko.i18n.renderString('interactionsList.direction.inboundAndOutbound')
        };

        self.startDateTime = ko.observable();
        self.endDateTime = ko.observable();
        self.duration = ko.observable();
        self.participantViews = ko.observableArray();
        self.agentName = ko.observable();
        self.externalName = ko.observable();
        self.agentNames = ko.observableArray();
        self.activeAgentNames = ko.observableArray();
        self.direction = ko.observable();
        self.active = ko.observable(false);
        self.hasCalls = ko.observable(false);
        self.hasChats = ko.observable(false);
        self.hasEmails = ko.observable(false);
        self.hasCallbacks = ko.observable(false);
        self.hasCobrowseSessions = ko.observable(false);
        self.hasScreenshares = ko.observable(false);
        self.hasMessages = ko.observable(false);
        self.hasRecordings = ko.observable(false);
        self.internalParticipantViews = ko.observableArray();
        self.externalParticipantViews = ko.observableArray();
        self.queueArray = ko.observableArray([]);
        self.queue = ko.observable();
        self.bothDirections = ko.observable(false);
        self.wrapUpCode = ko.observable();
        self.wrapUpCodeId = ko.observable();
        self.downloading = ko.observable(false);
        self.recordingInformation = ko.observableArray([]);
        self.today = moment().toISOString();
        self.conversationId = self.computed(function() {
            return ko.isObservable(conversationObservable) && conversationObservable() ?
                conversationObservable().id : null;
        });
        self.updatingRetention = ko.observable(false);
        self.canEditRetention = qualityAuth.canEditRetention();
        self.canEditScreenRecordingRetention = qualityAuth.canEditScreenRecordingRetention();
        self.canApplyProtectionStatus = ko.observable(qualityAuth.canApplyProtectionStatus());
        self.canRevokeProtectionStatus = ko.observable(qualityAuth.canRevokeProtectionStatus());
        self.canViewKeywordSet = qualityAuth.canViewKeywordSet;
        var timeFormatWithSeconds = ko.i18n.renderString('interactionsList.timeFormatSeconds');
        self.formattedStartDateTime = ko.observable();
        self.formattedEndDateTime = ko.observable();
        self.dnis = ko.observable('');
        self.ani = ko.observable('');
        self.messageType = ko.observable();

        var isConversationActive = function (conversation) {
            return !conversation.endTime;
        };

        // Sort by wrap-up state
        self.sortedParticipantViewsWithWrapup = self.computed(function() {
            return self.participantViews().sort(function (left, right) {
                if (left.awaitingWrapup() && right.hasWrapUpCode()) {
                    return 1;
                }
                if (left.hasWrapUpCode() && right.awaitingWrapup()) {
                    return -1;
                }
                return 0;
            });
        });

        var setDirection = function (direction) {
            if (_.isUndefined(self.direction())) {
                self.direction(direction);
            } else if (direction === "inbound" && self.direction() === "outbound") {
                self.direction("inbound / outbound");
                self.bothDirections(true);
            } else if (direction === "outbound" && self.direction() === "inbound") {
                self.direction("inbound / outbound");
                self.bothDirections(true);
            }
        };

        var addQueueName = function (name) {
            if (_.isUndefined(self.queue())) {
                self.queue(name);
            } else {
                self.queue(self.queue() + ko.i18n.renderString("interaction.list.delimiter") + name);
            }
        };

        var addWrapupName = function (name) {
            if (_.isUndefined(self.wrapUpCode())) {
                self.wrapUpCode(name);
            } else {
                self.wrapUpCode(self.wrapUpCode() + ko.i18n.renderString("interaction.list.delimiter") + name);
            }
        };

        // For active conversations, calculate the duration from startTime until now.
        // A setInterval will call this function on each row to update
        self.updateDuration = function () {
            if (self.active()) {
                var elapsedTime = moment().diff(self.startDateTime());
                self.duration(elapsedTime);
            }
        };

        self.displayWrapupInfo = function () {
            return _.some(ko.toJS(conversationObservable).participants, function (participant) {
                var participantView = _.find(self.participantViews(), function (view) {
                    return (view.participantId === participant.id);
                });

                return participantView && participantView.isInternal() &&
                    (participantView.hasWrapUpCode() || participantView.hasWrapUpNotes());
            });
        };

        self.awaitingAnyWrapups = function () {
            return _.some(ko.toJS(conversationObservable).participants, function (participant) {
                var participantView = _.find(self.participantViews(), function (view) {
                    return (view.participantId === participant.id);
                });
                return participantView && participantView.awaitingWrapup();
            });
        };

        self.navigateToInteraction = function (data) {
            if (data && data.id) {
                navHelper.navigateTo('#admin/interactions/' + data.id);
            }
        };

        var isCurrentUserOnCall = self.computed(function () {
            var conversation = ko.unwrap(conversationObservable);
            if (!userService.currentUser() ||
                !conversation ||
                !isConversationActive(conversation)) {

                return false;
            }

            var participants = ko.unwrap(conversation.participants);
            var activeParticipantForUser = _.find(participants, function (participant) {
                return (participant.userId === userService.currentUser().id &&
                    !participant.endTime);
            });

            return !!activeParticipantForUser;
        });

        self.canDownloadRecording = self.computed(function() {
            // Can download if the feature to enforce is off, or if they have the permission.
            var conversation = ko.unwrap(conversationObservable);
            if (!conversation || !conversation.divisionIds) {
                return false;
            }
            return qualityAuth.canDownloadRecording(conversation.divisionIds());
        });

        var hasMonitorCallPermission = self.computed(function() {
            var conversation = ko.unwrap(conversationObservable);
            if (!conversation || !conversation.divisionIds) {
                return true;
            }
            return qualityAuth.canMonitorCall(conversation.divisionIds());
        });

        var recordingOptionsToInformation = function(newRecs) {
            if(newRecs.length > 0) {
                var returnObject = [];
                _.each(options.recordingInformation(), function(recording){
                    var recordingObject = createRecordingObject(recording);
                    if (!_.isEmpty(recordingObject)){
                        returnObject.push(recordingObject);
                    }
                });
                self.recordingInformation(returnObject);
            } else {
                self.recordingInformation([]);
                self.hasRecordings(false);
            }
        };

        var sumScores = function(scoreProperty) {
            return function (result, keywordSpot) {
                return result + parseInt(keywordSpot[scoreProperty]);
            };
        };

        self.keywordSpots = options.keywordSpots;
        if (self.keywordSpots) {
            self.keywordSpotTotalAgentScore = self.computed(function () {
                return _.reduce(self.keywordSpots(), sumScores("agentScoreModifier"), 0);
            });
            self.keywordSpotTotalCustomerScore = self.computed(function () {
                return _.reduce(self.keywordSpots(), sumScores("customerScoreModifier"), 0);
            });
        }

        self.initRecording = self.computed(function(){
            if (ko.isObservable(options.recordingInformation) &&
                    options.recordingInformation() &&
                    options.recordingInformation().length > 0) {
                recordingOptionsToInformation(options.recordingInformation());
                self.subscribe(options.recordingInformation, recordingOptionsToInformation);
                _.debounce(function () { self.initRecording.dispose(); })();
            }
        });

        function pollFunction(callback, recordingObject){
            (function poll(){
                setTimeout(function(){
                    callback().then(function(recordingModel){
                        if (!recordingModel) {
                            poll();
                        } else {
                            recordingObject.downloading(false);
                            var link = document.getElementsByClassName("conversation-download-recording");
                            link[0].href = recordingModel.mediaUris.S.mediaUri;
                            link[0].click();
                        }
                    })
                    .catch(function (err) {
                        recordingObject.downloading(false);
                        errorCallbackFactory.createCallback({ namespace: "playback.downloadRecording"})(err);
                    });
                }, 3000);
            })();
        }

        function createRecordingObject(recording){
            var recordingObject = {};

            if (recording.isAcdVoicemail) {
                self.hasRecordings(self.hasRecordings() || !ko.unwrap(recording.isDeleted));
                return recording;
            }

            recordingObject.downloading = ko.observable(false);
            recordingObject.displayed = recording.isDisplayed;
            recordingObject.media = ko.i18n.renderString('conversationDetails.recordingInformation.mediaType.' + recording.media);

            recordingObject.download = function(recordingId, format, downloadFileName){
                recordingObject.downloading(true);
                var callbackFunction = function(){
                    return qualityManagementService.getDownloadRecording(self.conversationId(), recordingId, format, downloadFileName);
                };
                pollFunction(callbackFunction, recordingObject);
            };

            recordingObject.editingDownload = ko.observable(false);
            recordingObject.downloadFileName = ko.observable();
            recordingObject.editDownload = function(){
                recordingObject.editingDownload(true);
            };
            recordingObject.cancelDownloadEdit = function(){
                recordingObject.editingDownload(false);
            };
            recordingObject.fileFormatOptions = ko.observable([
                {text: ko.i18n.renderString('conversationDetails.wav'), value: "wav"},
                {text: ko.i18n.renderString('conversationDetails.webm'), value: "webm"}
            ]);

            recordingObject.selectedFileFormat = ko.observable();
            recordingObject.recordingId = recording.recordingId;
            recordingObject.canDownload = recording.recordingContext === "admin" &&
                                        (recording.recordingFileState === FileStateEnum.AVAILABLE ||
                                         recording.recordingFileState === FileStateEnum.RESTORED) &&
                                         recording.media === "audio";
            recordingObject.canDownloadRecording = self.canDownloadRecording;
            recordingObject.fileArchiveNotEditable = recording.recordingFileState === FileStateEnum.ARCHIVED ||
                                                    recording.recordingFileState === FileStateEnum.RESTORING ||
                                                    recording.recordingFileState === FileStateEnum.UPLOADING ||
                                                    recording.recordingFileState === FileStateEnum.DELETED ||
                                                    recording.recordingFileState === FileStateEnum.ARCHIVING;
            recordingObject.canUpdateRetention = recording.recordingFileState !== FileStateEnum.RESTORING &&
                                                 recording.recordingFileState !== FileStateEnum.ARCHIVING &&
                                                 recording.recordingFileState !== FileStateEnum.UPLOADING &&
                                                 recording.recordingFileState !== FileStateEnum.DELETED;
            recordingObject.canEditRetention = recording.media === 'screen' ? self.canEditScreenRecordingRetention : self.canEditRetention;
            recordingObject.isDeleted = recording.isDeleted;
            self.hasRecordings(self.hasRecordings() || !recordingObject.isDeleted);

            //Setup defaults of nulls
            recording.retentionInformation = recording.retentionInformation || {};

            recordingObject.updateAllRetention = ko.observable(false);
            recordingObject.archiveDate = ko.observable();
            recordingObject.hasArchiveDuration = ko.observable();
            recordingObject.archiveDateDisplay = ko.observable();
            recordingObject.deleteDate = ko.observable();
            recordingObject.recordingProtected = ko.observable();
            recordingObject.hasDeleteDuration = ko.observable();
            recordingObject.deleteDateDisplay = ko.observable();
            recordingObject.protectionStatus = ko.observable();
            recordingObject.lastSavedProtectionStatus = ko.observable();

            recordingObject.getProtectionStatus = function () {
                return conversationsProtectionService.getProtectionStatus(self.conversationId())
                    .then(function (response) {
                        if (response.length > 0) {
                            recordingObject.lastSavedProtectionStatus(true);
                            recordingObject.protectionStatus(true);
                        } else {
                            recordingObject.lastSavedProtectionStatus(false);
                            recordingObject.protectionStatus(false);
                        }
                    }).catch(function (error) {
                        recordingObject.protectionStatus(recordingObject.lastSavedProtectionStatus());
                        errorCallbackFactory.createCallback({namespace: "conversationDetails.getProtectionStatus.error.default"})(error);
                    });
            };

            featureToggleService.getFeatureToggle('qm.release.recording.deletionProtection')
                .then(function (enabled) {
                    if (enabled) {
                        return recordingObject.getProtectionStatus();
                    }
                });

            recordingObject.updateProtectionStatus = function(){
                return featureToggleService.getFeatureToggle('qm.release.recording.deletionProtection')
                    .then(function (enabled) {
                        if (enabled && ((self.canApplyProtectionStatus() && !recordingObject.lastSavedProtectionStatus()) ||
                            (self.canRevokeProtectionStatus() && recordingObject.lastSavedProtectionStatus()))) {
                            return conversationsProtectionService.updateProtectionStatus(self.conversationId(), recordingObject.protectionStatus())
                                .catch(function (error) {
                                    recordingObject.protectionStatus(recordingObject.lastSavedProtectionStatus());
                                    errorCallbackFactory.createCallback({namespace: "conversationDetails.updateProtectionStatus.error.default"})(error);
                                }).then(recordingObject.getProtectionStatus);
                        }
                    });
            };

            recordingObject.updateArchiveDate = function (archiveDate) {
                recordingObject.archiveDate(archiveDate || null);
                recordingObject.hasArchiveDuration(!!archiveDate);

                var archiveDateDisplay = recordingObject.archiveDate() ?
                    ko.i18n.formatDate(new Date(recordingObject.archiveDate())) :
                    ko.i18n.renderString('conversationDetails.retention.no.date');

                recordingObject.archiveDateDisplay(archiveDateDisplay);
            };

            recordingObject.updateDeleteDate = function (deleteDate) {
                recordingObject.deleteDate(deleteDate || null);
                recordingObject.hasDeleteDuration(!!deleteDate);

                var deleteDateDisplay = recordingObject.deleteDate() ?
                        ko.i18n.formatDate(new Date(recordingObject.deleteDate())) :
                        ko.i18n.renderString('conversationDetails.retention.no.date');

                recordingObject.deleteDateDisplay(deleteDateDisplay);
                recordingObject.minDeleteDate = recordingObject.archiveDate() ? moment(recordingObject.archiveDate()).add(1, 'day').toISOString() : moment().toISOString();
            };

            recordingObject.updateArchiveDate(recording.retentionInformation.archiveDate);
            recordingObject.lastSavedArchiveDate = recording.retentionInformation.archiveDate;
            recordingObject.updateDeleteDate(recording.retentionInformation.deleteDate);
            recordingObject.lastSavedDeleteDate = recording.retentionInformation.deleteDate;
            recordingObject.exportedDate = ko.observable(recording.retentionInformation.exportedDate);
            var exportedDateDisplay = recordingObject.exportedDate() ?
                    ko.i18n.formatDate(new Date(recordingObject.exportedDate())) :
                    ko.i18n.renderString('conversationDetails.retention.no.date');
            recordingObject.exportedDateDisplay = ko.observable(exportedDateDisplay);

            if (recordingObject.canEditRetention) {
                recordingObject.archiveDateEditor = self.subView(DateEditorView, recordingObject.archiveDate);
            }

            if (recordingObject.canEditRetention) {
                recordingObject.deleteDateEditor = recordingObject.deleteDateEditor || self.subView(DateEditorView, recordingObject.deleteDate);
            }

            recordingObject.validateDates = self.computed(function() {
                if (recordingObject.archiveDate()) {
                    if(moment(recordingObject.deleteDate()).startOf('day').subtract(1, 'second').isBefore(moment(recordingObject.archiveDate()))) {
                        recordingObject.deleteDate(moment(recordingObject.archiveDate()).add(1, 'day').toISOString());
                    }
                }
            });

            recordingObject.isRetentionModified = self.computed(function () {
                return (recordingObject.lastSavedArchiveDate || null) !== recordingObject.archiveDate() ||
                        (recordingObject.lastSavedDeleteDate || null) !== recordingObject.deleteDate() ||
                        recordingObject.lastSavedProtectionStatus() !== recordingObject.protectionStatus();
            });

            recordingObject.isEditingRetention = ko.observable(false);
            recordingObject.beginEditRetention = function() {
                recordingObject.isEditingRetention(true);
            };
            recordingObject.cancelEditRetention = function() {
                recordingObject.updateArchiveDate(recordingObject.lastSavedArchiveDate);
                recordingObject.updateDeleteDate(recordingObject.lastSavedDeleteDate);
                recordingObject.protectionStatus(recordingObject.lastSavedProtectionStatus());
                recordingObject.isEditingRetention(false);
            };
            return recordingObject;
        }

        self.updateRetentionOption = function(recordingInformation) {
            self.updatingRetention(true);
            var recordingInformationList = [recordingInformation];
            if (recordingInformation.updateAllRetention()) {
                recordingInformationList = _.filter(self.recordingInformation(), excludeFromRetentionUpdate);
            }

            var updatePromises = _.map(recordingInformationList, function(recordingInformationToUpdate) {
                var payload = {
                    archiveDate : recordingInformation.archiveDate() || undefined,
                    deleteDate : recordingInformation.deleteDate() || undefined,
                    recordingId : recordingInformationToUpdate.recordingId,
                    conversationId : self.conversationId()
                };
                if (payload.archiveDate === recordingInformationToUpdate.lastSavedArchiveDate &&
                payload.deleteDate === recordingInformationToUpdate.lastSavedDeleteDate) {
                    return new Promise.resolve();
                } else {
                    return qualityManagementService.updateRecording(payload)
                        .then(function(res){
                            recordingInformationToUpdate.lastSavedArchiveDate = ko.unwrap(res.archiveDate);
                            recordingInformationToUpdate.lastSavedDeleteDate = ko.unwrap(res.deleteDate);
                        })
                        .catch(function(error) {
                            errorCallbackFactory.createCallback({namespace: "conversationDetails.updateRetentionDuration"})(error);
                        }).finally(function () {
                            recordingInformationToUpdate.archiveDate(undefined);
                            recordingInformationToUpdate.deleteDate(undefined);
                            recordingInformationToUpdate.updateArchiveDate(recordingInformationToUpdate.lastSavedArchiveDate);
                            recordingInformationToUpdate.updateDeleteDate(recordingInformationToUpdate.lastSavedDeleteDate);
                        });
                }
            });

            var protectionStatusPromise = Promise.resolve();
            if (recordingInformation.lastSavedProtectionStatus() !== recordingInformation.protectionStatus()) {
                protectionStatusPromise = recordingInformation.updateProtectionStatus()
                    .then(function () {
                        self.recordingInformation().forEach( function (recordingObject) {
                            recordingObject.lastSavedProtectionStatus(recordingInformation.protectionStatus());
                            recordingObject.protectionStatus(recordingInformation.protectionStatus());
                            recordingObject.isEditingRetention(false);
                        });
                    });
            } else {
                recordingInformation.isEditingRetention(false);
            }

            Promise.all(updatePromises.concat([protectionStatusPromise])).finally(function() { self.updatingRetention(false); });
        };

        var excludeFromRetentionUpdate = function(recordingObject) {
            return !recordingObject.fileArchiveNotEditable || recordingObject.isAcdVoicemail;
        };

        self.removeArchiveDuration = function(recordingObject) {
            recordingObject.updateArchiveDate(null);
        };

        self.removeDeleteDuration = function(recordingObject) {
            recordingObject.updateDeleteDate(null);
        };

        self.addArchiveDuration = function(recordingObject) {
            recordingObject.updateArchiveDate(moment().toISOString());
        };

        self.addDeleteDuration = function(recordingObject) {
            var deleteDate = recordingObject.archiveDate() ? moment(recordingObject.archiveDate()).add(1,'day') : moment();
            recordingObject.updateDeleteDate(deleteDate);
        };

        var getParticipantViewChainNames = function (participantViewChain) {
            // filter for uniqueness, then get the name
            return participantViewChain.uniq(function(participantView) {
                return participantView.userId;
            })
            .map(function (participantView) {
                return participantView.userDisplayString() || participantView.name || ko.i18n.renderString('search.noName');
            })
            .value();
        };

        var clipboard = new Clipboard('.copy-address-target.copyable', {
            text: function (trigger) {
                return trigger.innerText;
            }
        });

        clipboard.on('success', function(e) {
            toasts.notifyMessage(ko.i18n.renderString('conversationDetails.participantAddress.copySuccess', {text: e.text}));
        });
        clipboard.on('error', errorCallbackFactory.createCallback({ namespace: "conversationDetails.participantAddress.copy"}));

        self.onDispose = function () {
            clipboard.destroy();
        };

        /**
         * END RETENTION DURATION SECTION
         */

        self.computed(function () {
            var conversation = ko.toJS(conversationObservable);
            var analyticsConversationDetails = ko.toJS(conversationDetailsV6);
            if (conversation) {
                if (self.id !== conversation.id){
                    self.id = conversation.id;
                    self.participantViews([]);
                    self.internalParticipantViews([]);
                    self.externalParticipantViews([]);
                }

                var isActive = isConversationActive(conversation);
                self.active(isActive);

                //get duration
                var startDate = moment(conversation.startTime);
                self.startDateTime(startDate.toDate());
                self.formattedStartDateTime(ko.i18n.formatDate(self.startDateTime(), timeFormatWithSeconds));

                if (!isActive) {
                    // Calculate duration for historical conversation
                    var endDate = moment(conversation.endTime);
                    self.endDateTime(endDate.toDate());
                    self.formattedEndDateTime(ko.i18n.formatDate(self.endDateTime(), timeFormatWithSeconds));
                    self.duration(endDate.diff(startDate));
                }

                if (conversation.participants) {

                    _.each(conversation.participants, function (participant) {
                        var participantView = _.find(self.participantViews(), function (view) {
                            return (view.participantId === participant.id);
                        });

                        var analyticsParticipant = null;
                        if (analyticsConversationDetails && analyticsConversationDetails.participants) {
                            analyticsParticipant = _.find(analyticsConversationDetails.participants, {'participantId': participant.id });
                        }

                        if (!participantView) {
                            participantView = self.subView(ParticipantView, conversation.id, participant, analyticsParticipant,
                                isCurrentUserOnCall, hasMonitorCallPermission, options);
                            self.participantViews.push(participantView);
                            if (participantView.isInternal()) {
                                self.internalParticipantViews.push(participantView);
                            } else if (participantView.isExternal()) {
                                self.externalParticipantViews.push(participantView);
                            }

                        } else {
                            participantView.updateParticipant(participant, analyticsParticipant);
                        }

                        if (participantView.dnis && (!/[a-z]/i.test(participantView.dnis))) {
                            var dnisValue = participantView.dnis.replace(/[-+ )(]/g,'');
                            if (!_.includes(self.dnis(), dnisValue)) {
                                if (!_.isEmpty(self.dnis())) {
                                    dnisValue = self.dnis() + ko.i18n.renderString("interaction.list.delimiter") + dnisValue;
                                }
                                self.dnis(dnisValue);
                            }
                        }

                        if (participantView.ani && (!/[a-z]/i.test(participantView.ani))) {
                            var aniValue = participantView.ani.replace(/[-+ )(]/g,'');
                            if (!_.includes(self.ani(), aniValue)) {
                                if (!_.isEmpty(self.ani())) {
                                    aniValue = self.ani() + ko.i18n.renderString("interaction.list.delimiter") + aniValue;
                                }
                                self.ani(aniValue);
                            }
                        }

                        if (participant.queueName && !_.includes(self.queueArray(), participant.queueName)){
                            self.queueArray.push(participant.queueName);
                            addQueueName(participant.queueName);
                        }

                        if (participant.wrapup && participant.wrapup.code) {
                            addWrapupName(getWrapupName(participant.wrapup));
                        }

                        if (participant.calls && participant.calls.length > 0) {
                            if (!_.isUndefined(participant.calls[0].direction) && !self.bothDirections()) {
                                setDirection(participant.calls[0].direction);
                            }
                            self.hasCalls(true);
                        }

                        // Set whether the conversation has a particular media type based on this participant.
                        self.hasChats(self.hasChats() || (!!participant.chats && participant.chats.length > 0));
                        self.hasEmails(self.hasEmails() || (!!participant.emails && participant.emails.length > 0));
                        self.hasCallbacks(self.hasCallbacks() || (!!participant.callbacks && participant.callbacks.length > 0));
                        self.hasCobrowseSessions(self.hasCobrowseSessions() || (!!participant.cobrowsesessions && participant.cobrowsesessions.length > 0));
                        self.hasScreenshares(self.hasScreenshares() || (!!participant.screenshares && participant.screenshares.length > 0));
                        self.hasMessages(self.hasMessages() || (!!participant.messages && participant.messages.length > 0));


                        // Support showing message type -- sms, line, facebook twitter
                        if (!self.messageType() && participantView.getMessageType()) {
                            var type = ko.i18n.renderString('conversationDetails.messageType.' + participantView.getMessageType());
                            if (type) {
                                self.messageType(type);
                            }
                        }
                    });

                    self.direction(getLocalizedDirection(self.direction()));

                    var internals = self.internalParticipantViews();
                    var externals = self.externalParticipantViews();

                    var internalChain = _(internals);
                    var activeInternalChain = internalChain.filter(function (ipv) {
                        return ipv.isActive();
                    });

                    self.agentNames(getParticipantViewChainNames(internalChain));
                    self.activeAgentNames(getParticipantViewChainNames(activeInternalChain));

                    if (internals.length > 0) {
                        self.agentName(internals[0].displayString());
                    } else {
                        self.agentName(ko.i18n.renderString('search.noName'));
                    }

                    if (externals.length > 0) {
                        self.externalName(externals[0].displayString());
                    } else {
                        self.externalName(ko.i18n.renderString('search.noName'));
                    }
                }
            }
        });
    });
    return ConversationDetailsView;
});

